import { AudioFormat, HdrType, StreamType, SubtitlesCodec, } from '@zattoo/playback-sdk';
import * as rakutenUtils from '../capability/rakuten';
import { StreamProvider } from './vod/stream-provider';
import { getCastStreamingProtocol, isRakutenStreamRequest, isRakutenVodTrailerRequest, } from './request/utils';
export const getAudioLanguageParamRakutenVodTrailer = (audioLanguages, preferredAudioLanguage) => {
    if (!preferredAudioLanguage || !audioLanguages.includes(preferredAudioLanguage)) {
        return audioLanguages[0];
    }
    return preferredAudioLanguage;
};
export const getAudioLanguageParamRakutenVod = (streamingOptions, preferredAudioLanguage) => {
    const availableAudioLanguages = [
        ...new Set(streamingOptions.flatMap((option) => option.audio_language_codes)).values(),
    ];
    if (!preferredAudioLanguage || !availableAudioLanguages.includes(preferredAudioLanguage)) {
        return availableAudioLanguages[0];
    }
    return preferredAudioLanguage;
};
export const getRakutenAudioLanguageParam = (streamRequestInfo, preferredAudioLanguage) => {
    if (!isRakutenStreamRequest(streamRequestInfo)) {
        return preferredAudioLanguage;
    }
    if (isRakutenVodTrailerRequest(streamRequestInfo)) {
        return getAudioLanguageParamRakutenVodTrailer(streamRequestInfo.audioLanguages, preferredAudioLanguage);
    }
    return getAudioLanguageParamRakutenVod(streamRequestInfo.streamingOptions, preferredAudioLanguage);
};
export const watchParamsFromStreamType = (streamType) => {
    return {
        stream_type: streamType,
        https_watch_urls: true,
    };
};
export const watchParamsFromPlayOptions = (playOptions, streamProvider) => {
    const params = {};
    if (playOptions?.pin) {
        params.youth_protection_pin = playOptions.pin;
    }
    if (playOptions?.tcString) {
        params.tc_string = playOptions.tcString;
    }
    if (playOptions?.gt12ChannelInfo?.isGT12BV) {
        params.with_schedule = true;
    }
    if (playOptions?.castEnabled) {
        params.cast_stream_type = getCastStreamingProtocol(playOptions, streamProvider);
    }
    if (playOptions.thumbnails !== undefined) {
        params.thumbnail_seeking = playOptions.thumbnails;
    }
    return params;
};
export const mapHdcpVersionToType = (version) => {
    if (!version) {
        return null;
    }
    if (version.toLowerCase() === 'unprotected') {
        return 'Unprotected';
    }
    if (Number(version) >= 1 && Number(version) < 2) {
        return 0;
    }
    if (Number(version) >= 2) {
        return 1;
    }
    return null;
};
export const watchParamsDolby = (capability) => {
    const params = {};
    if (capability.dolbySupported) {
        params.enable_eac3 = true;
    }
    return params;
};
export const watchParamsSubtitles = (capability) => {
    const params = {};
    if (capability.subtitlesCodecs) {
        params.vtt = capability.subtitlesCodecs.some((codec) => codec === SubtitlesCodec.VTT);
        params.ttml = capability.subtitlesCodecs.some((codec) => codec === SubtitlesCodec.TTML);
    }
    return params;
};
export const watchParamsHdcp = (capability, streamProvider) => {
    const params = {};
    if (!capability.hdcpLevel) {
        return params;
    }
    if (streamProvider === StreamProvider.RAKUTEN) {
        const hdcpType = rakutenUtils.maxHdcpType(capability);
        params.max_hdcp_type = hdcpType;
        return params;
    }
    const hdcpType = mapHdcpVersionToType(capability.hdcpLevel.connectedVersion);
    if (hdcpType !== null) {
        params.max_hdcp_type = hdcpType;
    }
    return params;
};
export const watchParamsDrm = (streamType, capability) => {
    const params = {};
    if (streamType === StreamType.DASH_WIDEVINE ||
        streamType === StreamType.DASH_PLAYREADY) {
        const maxDrmLevel = capability.streamTypeInformation.find((info) => info.type === streamType)?.maxSecurityLevel;
        if (maxDrmLevel) {
            // @ts-expect-error string is fine
            params.max_drm_lvl = maxDrmLevel;
        }
    }
    return params;
};
export const watchParamsHdr = (capability) => {
    const params = {};
    const supportedHdrTypes = [HdrType.DOLBY_VISION, HdrType.HDR10];
    if (capability.hdrTypesSupported?.some((hdr) => supportedHdrTypes.includes(hdr))) {
        if (capability.hdrTypesSupported.includes(HdrType.DOLBY_VISION)) {
            params.hdr_type = HdrType.DOLBY_VISION;
        }
        else if (capability.hdrTypesSupported.includes(HdrType.HDR10)) {
            params.hdr_type = HdrType.HDR10;
        }
    }
    else {
        params.hdr_type = HdrType.NONE;
    }
    return params;
};
export const watchParamsRakuten = (streamRequestInfo, capability, playOptions) => {
    const params = {};
    if (capability.maxQualityPlayable) {
        params.quality = capability.maxQualityPlayable;
    }
    params.lang = getRakutenAudioLanguageParam(streamRequestInfo, playOptions.preferredAudioLanguage);
    if (!params.lang) {
        throw new Error('Audio language is required for Rakuten VOD streams');
    }
    params.subtitle_protocol = 'HTTPS';
    // Rakuten only has one audio track per stream, so
    // stereo/dolby preference must be part of the request
    if (playOptions.preferredAudioFormat === AudioFormat.STEREO) {
        params.enable_eac3 = false;
    }
    params.hdr_type = watchParamsHdr(capability).hdr_type;
    return params;
};
export const watchParamsFromCapability = (streamType, capability, streamProvider) => {
    return {
        ...watchParamsDolby(capability),
        ...watchParamsSubtitles(capability),
        ...watchParamsHdcp(capability, streamProvider),
        ...watchParamsDrm(streamType, capability),
    };
};
/**
 * Picks highest rate from watch urls list
 * Remark: maxrate 0 means unlimited
 */
export const pickHighestRate = (watchUrls) => {
    if (watchUrls.length === 0) {
        return null;
    }
    const sort = (infoA, infoB) => {
        const maxRateA = infoA.maxrate || Number.POSITIVE_INFINITY;
        const maxRateB = infoB.maxrate || Number.POSITIVE_INFINITY;
        // order descending
        return maxRateB - maxRateA;
    };
    return watchUrls.slice().sort(sort)[0] ?? null;
};
export const mediaItemFromWatchResponse = (assetType, watchResponse, dynamic) => {
    const highestRate = pickHighestRate(watchResponse.stream.watch_urls);
    const url = watchResponse.stream.url ??
        highestRate?.url;
    const licenseUrl = watchResponse.stream.license_url ??
        highestRate?.license_url;
    return {
        url,
        licenseUrl,
        prePadding: watchResponse.stream.padding?.pre ?? 0,
        postPadding: watchResponse.stream.padding?.post ?? 0,
        assetType,
        dynamic: dynamic ?? false,
    };
};
const gt12MarkersFromSchedule = (scheduleInfo) => {
    if (!scheduleInfo || !scheduleInfo.ad_breaks) {
        return [];
    }
    return scheduleInfo.ad_breaks.map((adBreak) => ({
        adsEnd: adBreak.ads_end ? adBreak.ads_end * 0.001 : undefined,
        end: adBreak.end * 0.001,
        promoStart: adBreak.promo_start ? adBreak.promo_start * 0.001 : undefined,
        start: adBreak.start * 0.001,
    }));
};
const gt12ScheduleFromWatchResponse = (watchResponse) => {
    if (!watchResponse.stream.schedule) {
        return [];
    }
    return watchResponse.stream.schedule.map((schedule) => ({
        programStart: schedule.program_start * 0.001,
        markers: gt12MarkersFromSchedule(schedule),
    }));
};
export const gt12ChannelInfo = (zapiPlayOptions, watchResponse) => {
    if (!zapiPlayOptions.gt12ChannelInfo) {
        return undefined;
    }
    return {
        ...zapiPlayOptions.gt12ChannelInfo,
        forwardSeekingAllowed: watchResponse.stream.forward_seeking ?? true,
        schedule: gt12ScheduleFromWatchResponse(watchResponse),
    };
};
